import moment from 'moment';
import noop from 'lodash/noop';
import { GRANULARITY, GRAPH_TYPES } from '../../consts';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import { getSeries, getYData } from './utils/series';
import { isMulti } from 'common/utils/utils';
import { yFormatter } from '../utils/formatters';
import {
  getGranularityByDates,
  isMedianMetric
} from '../../custom-report.util';
import useWorkingDaysSettings from 'views/root/views/home/hooks/useWorkingDaysSettings';
import { STORY_POINTS_CALCULATION_STRATEGY } from '@linearb/linjs-common';

const useWidgetPropsByGraphType = ({
  widgetMetrics: {
    data: metricsData,
    name: metricName,
    avgByName,
    percentOff,
    computedBy,
    agg: widgetAggregation,
    searchRanges: { searchRange }
  },
  graph,
  filters,
  onPointClick,
  onPointHover,
  onAreaSelect,
  axisTitle = '',
  shouldHideClickableMessage
}) => {
  const workingDaysSetting = useWorkingDaysSettings({
    teams: filters.globallySelectedTeams
  });
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { selectedGranularity: granularity, selectedTimeRanges } = filters;

  const getEndDateForTooltip = defaultEndValue => {
    const { end_date } = selectedTimeRanges;
    return granularity === GRANULARITY.ITERATIONS
      ? moment(defaultEndValue)
      : moment.min(moment(end_date), moment(defaultEndValue));
  };

  const metricsCalculationConfigWithAgg = {
    ...metricsCalculationConfig,
    metricsCalculationStrategy:
      widgetAggregation ?? metricsCalculationConfig.metricsCalculationStrategy
  };

  const { metricsCalculationStrategy } = metricsCalculationConfigWithAgg;

  const series = getSeries({
    widgetMetrics: metricsData,
    metricName,
    avgByName,
    percentOff,
    computedBy,
    metricsCalculationStrategy,
    filters,
    graph
  });

  const isMultiSeries = isMulti(series);

  const percentOffName = percentOff?.name ?? null;
  const yData = getYData({
    metrics: metricsData,
    metricName,
    avgByName,
    percentOff: percentOff?.metrics?.[0]?.name ?? null,
    computedBy,
    graphId: graph.id,
    metricsCalculationStrategy
  });

  const isClickable = !!onPointClick;
  const {
    name,
    color,
    displayLabel,
    overridePercentName,
    unitType,
    calculation_strategy: calculationStrategy
  } = graph;

  const displayLabelByCalculationStrategy =
    calculationStrategy === STORY_POINTS_CALCULATION_STRATEGY
      ? 'Story Points'
      : displayLabel;

  const defaultProps = {
    isMultiSeries,
    shouldHideClickableMessage,
    searchRange,
    granularity: getGranularityByDates({
      ...selectedTimeRanges,
      selectedGranularity: granularity
    }),
    getEndDateForTooltip
  };

  const barChartsProps = {
    yData,
    onPointClick,
    displayName: name,
    yFormatter: yFormatter(unitType),
    graphColor: color,
    displayLabel: displayLabelByCalculationStrategy ?? name,
    metricName,
    metrics: graph.metrics,
    percentOffName,
    overridePercentName,
    metricsCalculationConfig: metricsCalculationConfigWithAgg
  };

  const areaChartProps = {
    series,
    isClickable,
    yFormatter: yFormatter(unitType),
    isMedianMetric,
    displayLabel: displayLabel ?? name,
    percentOffName,
    metricName,
    metrics: graph.metrics,
    metricsCalculationConfig: metricsCalculationConfigWithAgg,
    onPointClick,
    onPointHover,
    onAreaSelect,
    graphColor: color,
    toggleDisabledSeriesIdsByMetricName: noop // TODO: change
  };

  const stackedAreaProps = {
    metrics: metricsData,
    graphId: graph.id,
    annotations: [],
    axisTitle,
    unitDescription: graph.unitDescription,
    isClickable,
    metrics: graph.metrics
  };

  return {
    defaultGraph: { ...defaultProps, ...areaChartProps, workingDaysSetting },
    [GRAPH_TYPES.BAR]: {
      ...defaultProps,
      ...barChartsProps,
      workingDaysSetting
    },
    [GRAPH_TYPES.STACKED]: {
      ...defaultProps,
      ...stackedAreaProps,
      workingDaysSetting
    },
    isMultiSeries
  };
};

export default useWidgetPropsByGraphType;
